import { getPagination, type Paginated } from '@deque/pagination-utils';
import { useCallback, useEffect, useRef } from 'react';
import { type HttpError } from '~/common/api-client';
import useAPI from '~/common/hooks/useAPI';
import {
  EXPORTS_NOT_CONFIGURED_ERROR_CODE,
  listSecurityEventLogExports,
  type SecurityEventLogExport
} from '~/common/utils/admin-client/get-security-event-log-exports';

const POLL_INTERVAL_MS = 5000;

interface UseSecurityLogExportsProps {
  token: string;
  enterpriseId: string | null;
  page?: number;
  per_page?: number;
}

interface UseSecurityLogExportsResponse {
  exports: SecurityEventLogExport[];
  totalItems: number;
  loading: boolean;
  /**
   * True while nothing displayable exists for the current inputs — the first
   * fetch, or retained data that belongs to a previously selected enterprise
   * or page. Poll-driven refreshes flip `loading` but not this, so callers can
   * keep the table on screen instead of flashing a loader every poll.
   */
  initialLoading: boolean;
  error: Error | null;
  /**
   * True when the backend reports export storage is not configured — an
   * expected environment state, surfaced separately so callers can render a
   * "not configured" message instead of a generic error. Keyed off the
   * backend's error_code rather than the bare 503 status, which proxies and
   * maintenance mode share.
   */
  notConfigured: boolean;
  refresh: () => void;
}

/**
 * The loader tags each page with the inputs that produced it. useAPI keeps the
 * previous `data` while a new loader runs (and nulls it when a fetch fails),
 * so the tag is what distinguishes "current selection's page" from "leftover
 * data for another enterprise/page".
 */
interface TaggedPage {
  enterpriseId: string;
  page: number | undefined;
  result: Paginated<SecurityEventLogExport[]>;
}

const isActive = (e: SecurityEventLogExport): boolean =>
  e.status === 'pending' || e.status === 'in_progress';

/**
 * Lists an enterprise's security log exports and polls while any are still in
 * flight. Polling stops once every export is terminal and is cleared on unmount;
 * a tick is skipped while a refresh is already in flight, and a failed tick
 * keeps the last successful page (and the poll loop) alive.
 */
export const useSecurityLogExports = ({
  token,
  enterpriseId,
  page,
  per_page
}: UseSecurityLogExportsProps): UseSecurityLogExportsResponse => {
  const dataLoader = useCallback(async (): Promise<TaggedPage> => {
    const result = await listSecurityEventLogExports({
      token,
      // Skipped via `skipRequest` below when null, so the cast is safe.
      enterpriseId: enterpriseId as string,
      page,
      per_page
    });
    return { enterpriseId: enterpriseId as string, page, result };
  }, [token, enterpriseId, page, per_page]);

  const { data, loading, error, refresh } = useAPI(dataLoader, !enterpriseId);

  const matchesInputs = (tagged: TaggedPage | null): tagged is TaggedPage =>
    tagged !== null &&
    tagged.enterpriseId === enterpriseId &&
    tagged.page === page;

  // Retain the last successful page so one failed poll doesn't blank the
  // table to its empty state (useAPI nulls `data` on any error) or, via
  // `hasActive` below, silently kill the poll loop while an export is still
  // running. Only a failure for the SAME inputs falls back; a failed fetch of
  // a newly selected enterprise/page stays empty.
  const lastGoodRef = useRef<TaggedPage | null>(null);
  useEffect(() => {
    if (data) {
      lastGoodRef.current = data;
    }
  }, [data]);

  const fresh = matchesInputs(data) ? data : null;
  const retained =
    !data && error && matchesInputs(lastGoodRef.current)
      ? lastGoodRef.current
      : null;
  const current = fresh ?? retained;

  const exports = current?.result ?? [];
  const totalItems = (current && getPagination(current.result))?.total ?? 0;
  const hasActive = exports.some(isActive);
  const notConfigured =
    (error as HttpError | null)?.error_code ===
    EXPORTS_NOT_CONFIGURED_ERROR_CODE;

  // Keep a live view of `loading` so the interval can skip overlapping refreshes
  // without re-creating itself every load.
  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  useEffect(() => {
    if (!hasActive) {
      return;
    }
    const id = setInterval(() => {
      if (!loadingRef.current) {
        refresh();
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasActive, refresh]);

  return {
    exports,
    totalItems,
    loading,
    initialLoading:
      (loading && data === null && !retained) ||
      (data !== null && !matchesInputs(data)),
    error: notConfigured ? null : error,
    notConfigured,
    refresh
  };
};
